/**
 * AnimationEventProcessor - handles event markers during animation playback.
 * Emits events when animation actions cross marker times.
 */
import { Delegate } from '../utils/Delegate.js';

import type { AnimationEventMarker, AnimationEventPayload } from './AnimationTypes.js';
import type * as THREE from 'three';

export class AnimationEventProcessor {
  private actionEventTimes: WeakMap<THREE.AnimationAction, number> = new WeakMap();
  private eventMarkers: Record<string, AnimationEventMarker[]> = {};
  public onAnimationEvent: Delegate<[AnimationEventPayload], void> = new Delegate();

  public setMarkers(markers: Record<string, AnimationEventMarker[]>): void {
    this.eventMarkers = markers;
  }

  public sanitize(events: Record<string, AnimationEventMarker[]>): Record<string, AnimationEventMarker[]> {
    const sanitized: Record<string, AnimationEventMarker[]> = {};
    for (const [clipName, markers] of Object.entries(events)) {
      if (!Array.isArray(markers)) continue;
      const validMarkers = markers
        .filter(marker => marker && typeof marker.name === 'string' && typeof marker.time === 'number')
        .map(marker => ({
          name: marker.name,
          time: Math.max(0, Math.min(1, marker.time)),
          tag: marker.tag,
          data: marker.data
        }))
        .sort((a, b) => a.time - b.time);
      if (validMarkers.length > 0) {
        sanitized[clipName] = validMarkers;
      }
    }
    return sanitized;
  }

  public resetActionTime(action: THREE.AnimationAction): void {
    this.actionEventTimes.set(action, 0);
  }

  public clearActionTimes(): void {
    this.actionEventTimes = new WeakMap();
  }

  public process(actions: THREE.AnimationAction[]): void {
    if (Object.keys(this.eventMarkers).length === 0) return;

    for (const action of actions) {
      if (!action.isRunning()) continue;
      if (action.getEffectiveWeight() <= 0.001) continue;

      const clip = action.getClip();
      const markers = this.eventMarkers[clip.name];
      if (!markers || markers.length === 0) continue;

      const duration = clip.duration;
      if (duration <= 0) continue;

      const rawTime = action.time;
      const effectiveTime = action.timeScale < 0 ? Math.max(0, duration - rawTime) : rawTime;
      const lastTime = this.actionEventTimes.get(action);

      if (lastTime === undefined) {
        this.actionEventTimes.set(action, effectiveTime);
        continue;
      }

      if (effectiveTime >= lastTime) {
        this.emitEventsInRange(clip.name, markers, lastTime, effectiveTime, duration);
      } else {
        // Triggers when effectiveTime goes backwards relative to the last tick.
        // - Loop wraparound on a looping clip (time jumps from near duration back to 0).
        // - A reset/restart (e.g., action.reset() or a new play on the same action).
        // - Abrupt seek/crossfade that rewinds the action time.
        this.emitEventsInRange(clip.name, markers, lastTime, duration, duration);
        this.emitEventsInRange(clip.name, markers, 0, effectiveTime, duration);
      }

      this.actionEventTimes.set(action, effectiveTime);
    }
  }

  private emitEventsInRange(
    clipName: string,
    markers: AnimationEventMarker[],
    startTime: number,
    endTime: number,
    duration: number
  ): void {
    if (endTime <= startTime) return;

    for (const marker of markers) {
      const markerTime = marker.time * duration;
      if (markerTime > startTime && markerTime <= endTime) {
        this.onAnimationEvent.invoke({
          clipName,
          eventName: marker.name,
          normalizedTime: marker.time,
          timeSeconds: markerTime,
          tag: marker.tag,
          data: marker.data
        });
      }
    }
  }
}
